import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { TRUSTED_DEVICE_DAYS } from '@pulsepoint/shared';
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { useForgetDevices, useTwoFactor } from '../../api/auth';
import { ApiError } from '../../api/client';
import { useDialog } from '../../components/dialogs/DialogProvider';
import { useToast } from '../../components/Toast';
import { formatAgo, formatDate } from '../../lib/format';
import { colours, radius } from '../../theme';

/** "Chrome on Windows", roughly, from the browser's user agent. */
function deviceName(ua: string | null): string {
  if (!ua) return 'A browser';
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'A browser';
  const os = /iPhone|iPad/.test(ua) ? 'iPhone or iPad' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'Mac' : /Linux/.test(ua) ? 'Linux' : '';
  return os ? `${browser} on ${os}` : browser;
}

/**
 * Your account, Two-factor sign-in: when it was set up, and the devices that skip the code
 * (ticked "Remember this device"), each of which can be forgotten.
 */
export function TwoFactorSection() {
  const tf = useTwoFactor();
  const forget = useForgetDevices();
  const dialog = useDialog();
  const toast = useToast();
  const devices = tf.data?.devices ?? [];
  // From the account menu: scroll here.
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === '#two-factor') document.getElementById('two-factor')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [hash]);

  const run = async (id?: string) => {
    try {
      await forget.mutateAsync(id);
      toast(id ? 'That device will ask for a code next time.' : 'Every device will ask for a code next time.');
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Something went wrong.', 'error');
    }
  };

  return (
    <Box id="two-factor" sx={{ mt: 6 }}>
      <Typography variant="h2">Two-factor sign-in.</Typography>
      {tf.data && !tf.data.required ? (
        <Typography color="textSecondary" sx={{ mt: 0.5, maxWidth: 640 }}>
          An Infinity EV admin turned two-factor sign-in off for you: your password alone signs you in. Ask them if you'd like it back on.
        </Typography>
      ) : (
        <>
      <Typography color="textSecondary" sx={{ mt: 0.5, mb: 3, maxWidth: 640 }}>
        {tf.data?.enabledAt ? `Set up on ${formatDate(tf.data.enabledAt)}. ` : ''}
        You sign in with your password and a code from your authenticator app. Devices where you ticked “Remember this device” skip the code for {TRUSTED_DEVICE_DAYS} days. Lost your phone? Ask an Infinity EV admin to reset it.
      </Typography>
      {devices.length === 0 ? (
        <Typography color="textSecondary">No remembered devices: the code is asked for every time.</Typography>
      ) : (
        <Box sx={{ display: 'grid', gap: 2, maxWidth: 640 }}>
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, border: `1px solid ${colours.hairline}`, borderRadius: `${radius.panel}px` }}>
            {devices.map((d, i) => (
              <Box component="li" key={d.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.25, borderTop: i ? `1px solid ${colours.hairline}` : 0 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 500 }}>
                    {deviceName(d.userAgent)}
                    {d.current && <Typography component="span" color="textSecondary"> (this one)</Typography>}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Remembered {formatDate(d.createdAt)}{d.lastUsedAt ? ` · last used ${formatAgo(d.lastUsedAt)}` : ''} · until {formatDate(d.expiresAt)}
                  </Typography>
                </Box>
                <Button size="small" onClick={() => void run(d.id)} disabled={forget.isPending}>Forget</Button>
              </Box>
            ))}
          </Box>
          <Box>
            <Button
              onClick={async () => {
                if (await dialog.confirm({ title: 'Forget every device?', message: 'Each one asks for a code at the next sign-in, this one included.', confirmLabel: 'Forget them all' })) void run();
              }}
              disabled={forget.isPending}
            >
              Forget all devices
            </Button>
          </Box>
        </Box>
      )}
        </>
      )}
    </Box>
  );
}
